// **Home — rounds mode's entry screen** (`client-home`, `home-and-single-player`; UI.md §6), at the
// `/` route the live Lobby occupies in its own root.
//
// ## What this screen became, and why it is smaller
//
// It was built around tournaments: join with a code, start a new tournament, recover host access, plus
// the three sheets those needed. The game being shown is single-player — practise in a sandbox, then
// fight a bot — so three of those controls were operator plumbing a player never needs, and the join
// group was the entry path to a mode nobody in the room is playing.
//
// So Home now offers **exactly two things a player can play**: the ranked sandbox and the
// leaderboard. Nothing else. A screen whose first decision is "which of these six controls is for me"
// has already failed the person who just opened it.
//
// **The computer match was the third, and it is gone from here** (WAG-108). Same relocation logic as
// the tournament controls above, and for the reason the `/versus` route's own note already gave: a
// rounds-mode server cannot host a live pvp match without handing the practice sandbox the pvp world,
// so on the deployment a player actually opens, the CTA led to a page explaining it could not be
// played. The route is still served — reachable by typing the pathname, on `/demo`'s footing — and the
// live pvp stack is untouched. What is deleted is the promise on the first screen anyone sees.
//
// **Nothing was deleted, it was relocated.** Tournament creation, the one-time join-code reveal and
// host recovery all live in the `/demo` composition root, which already owned the create-and-host
// bootstrap and `recoverHostCredential` — and host recovery in particular is the **only** credential
// recovery path in the system, because a member credential is stored as a hash and cannot be read
// back. The tournament stack itself stays compiled and tested; it is simply not reachable from here.
//
// **Presentational, exactly as `Lobby` is** (design **D-a**). Home constructs no `RoundsHttpClient`,
// no credential store and no connection: it takes the composition root's entry points
// ({@link HomeEntryPoints}) and renders. That is why the tests that matter for this screen live at the
// root, not here — every behaviour arrives through an injected entry point, so a component test cannot
// catch a wiring fault.
//
// ## The one state that is not a CTA
//
// A **sign-in** control renders when the composition root reports that a token is required and none was
// presented. It is not a third affordance and it is not the join group's sign-in hint returning: on a
// gated deployment every request this screen's CTAs make is refused without a token, and this is
// the only interactive sign-in path the application has. Deleting it with the join group would have
// left a gated deployment with controls that cannot work and no way to fix it.

import { useState, type CSSProperties, type ReactNode } from 'react';
import type { IdentityState } from '../../net/auth-identity';
import type {
  HomePracticeEntry,
  PracticeBeginOutcome,
} from '../../app/rounds/practice-entry';
import { Button } from '../components/core/Button';
import { PracticeSheet, type PracticeSurface } from '../home/PracticeSheet';
import { headerScrimCss, veilCss } from '../home/veil';
import festivalLogoVertical from '../../../../festival-assets/logo-vertical.svg';

/** The composition root's entry points. Each one owns the transport, the credential store and the
 * navigation Home is forbidden to own; Home owns the copy and the refusal rendering. */
export interface HomeEntryPoints {
  /** Begin an interactive sign-in. Only rendered when the server reports that a token is required
   * and none was presented. The root owns MSAL; Home owns the control. */
  signIn?(): void;
  /**
   * The practice path's entry points (`client-practice`). Optional for the same reason the others are
   * inert on a bare mount: absent, the CTA still draws — because a screen that hides a control while
   * its wiring lands teaches the player it does not exist — and reaches nothing.
   *
   * Home constructs **none** of what these own: not the capability gate, not the practice credential
   * store, not the simulation worker, not the local trace store (`client-home`).
   */
  practice?: HomePracticeEntry;
  /** Open the leaderboard. */
  openLeaderboard?(): void;
}

/** The inert default: a bare mount (a test, or the moment before the root's seams arrive) renders
 * a fully drawn, immediately usable screen that simply reaches nothing. The
 * `INERT_CONTROLLER` convention every other route already follows. */
const INERT_ENTRY: HomeEntryPoints = {};

export interface HomeProps {
  entry?: HomeEntryPoints;
  /**
   * **The identity state the composition root resolved** (`client-home`, `client-app`).
   *
   * A prop, never a fetch: Home is presentational and constructs no transport.
   *
   * Only `required` changes what is rendered, and it only *adds* the sign-in control. Every other
   * state — `unresolved`, `disabled`, `authenticated` — draws the same two CTAs, which is the safe
   * default in the only sense available to a client: the server is the authority on whether a token is
   * wanted, so a screen that guessed would either hide a control that works or offer one that cannot.
   */
  identity?: IdentityState;
  /** The ambient background layer the root supplies. Omitted renders the static season-ground
   * fill, which is the same layout with the same chrome (**D10**). */
  background?: ReactNode;
  /**
   * The instant the practice sheet's "how long ago" phrases are measured against.
   *
   * A plain number rather than a clock seam: Home reads no clock of its own anywhere else, and the
   * retention copy is the one place it would need to. A test passes a fixed instant so
   * "6 days ago" is a fact rather than a function of when the suite ran.
   */
  now?: number;
}

/**
 * Where the practice flow is.
 *
 * `busy` is the gate resolving — the capability probe plus the open request — and it is a state
 * rather than a spinner inside a sheet because in the ordinary case **there is no sheet**: a
 * successful open takes the player straight into the sandbox, and the only surfaces left are the two
 * refusals.
 */
type PracticeState =
  | { kind: 'closed' }
  | { kind: 'busy' }
  | { kind: 'open'; surface: PracticeSurface; busy: boolean };

export function Home({
  entry = INERT_ENTRY,
  identity = { kind: 'unresolved' },
  background,
  now = Date.now(),
}: HomeProps = {}) {
  const [practice, setPractice] = useState<PracticeState>({ kind: 'closed' });
  /** A transport failure from the practice gate, said beside the CTA that caused it. Never a
   * surface of its own: there is nothing to show yet, and an empty sheet would read as a session. */
  const [practiceNotice, setPracticeNotice] = useState<string | null>(null);

  /**
   * **A token is required and this player has none** (`client-home`).
   *
   * Home does **not** navigate away of its own accord. An entry screen that redirects on arrival
   * cannot be looked at, and an automatic redirect that fails to establish a session has nowhere to
   * land but back here. The player asks, once.
   */
  const needsSignIn = identity.kind === 'required';

  // --- the practice flow (`client-practice`) --------------------------------------------
  //
  // Every branch here is a *rendering* decision over the root's typed outcome. Home never decides
  // whether a device is capable, whether a sandbox exists, or whether one expired — it is told, and
  // the exhaustive switch is what makes "an expired session is never drawn as an empty new one" a
  // structural property rather than a thing to remember.

  const applyPracticeOutcome = (outcome: PracticeBeginOutcome): void => {
    switch (outcome.kind) {
      case 'refused':
        setPractice({ kind: 'open', surface: { kind: 'refused', report: outcome.report }, busy: false });
        return;
      case 'opened':
        // **No sheet.** The root has already taken the player into the sandbox; there is nothing left
        // to ask them, so an intermediate surface whose only action is "continue" would be a tap
        // charged for nothing.
        setPractice({ kind: 'closed' });
        return;
      case 'expired':
        setPractice({
          kind: 'open',
          surface: { kind: 'expired', expiredAt: outcome.expiredAt, retentionDays: outcome.retentionDays },
          busy: false,
        });
        return;
      case 'unavailable':
        // No surface opens. There is no honest one: the device may be fine and the sandbox may
        // exist, and drawing anything session-shaped here would claim one was opened.
        setPractice({ kind: 'closed' });
        setPracticeNotice(outcome.message);
        return;
    }
  };

  const beginPractice = async (): Promise<void> => {
    if (entry.practice === undefined) {
      return;
    }
    setPracticeNotice(null);
    setPractice({ kind: 'busy' });
    try {
      applyPracticeOutcome(await entry.practice.begin());
    } catch {
      setPractice({ kind: 'closed' });
      setPracticeNotice('Couldn’t open your sandbox. Check this browser’s storage settings and try again.');
    }
  };

  const startOverPractice = async (): Promise<void> => {
    if (entry.practice === undefined || practice.kind !== 'open') {
      return;
    }
    setPractice({ ...practice, busy: true });
    try {
      applyPracticeOutcome(await entry.practice.startOver());
    } catch {
      setPractice({ ...practice, busy: false });
      setPracticeNotice('Couldn’t start over. Your existing sandbox is unchanged.');
    }
  };

  return (
    <div data-testid="home-screen" style={SCREEN_STYLE}>
      <div data-testid="home-background" style={BACKGROUND_STYLE}>
        {background}
      </div>
      {/* The veil is a **gradient, not a wash** (**D-h**): near-clear over the colony at the top,
          thickening under the controls. A uniform veil buys legibility by killing the aliveness
          that justifies having a background at all. */}
      <div data-testid="home-veil" style={VEIL_STYLE} />
      {/* The header scrim: a second, **local** paper layer behind the Festival/Waggle branding. It
          is gone by 24% of the screen height, so the colony below it is untouched — thickening the
          global veil instead would be panel B, which passes contrast and is rejected anyway. */}
      <div data-testid="home-header-scrim" style={HEADER_SCRIM_STYLE} />

      <div style={STACK_STYLE}>
        <header data-testid="home-branding" style={BRANDING_STYLE}>
          <img src={festivalLogoVertical} alt="Northrop Festival" style={FESTIVAL_LOGO_STYLE} />
          <h1 style={WORDMARK_STYLE}>Waggle</h1>
        </header>

        <div style={{ flex: 1 }} />

        {needsSignIn && (
          // **Not a third CTA.** On a gated deployment every request the two below make is refused
          // without a token, and this is the application's only interactive sign-in path.
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Button variant="secondary" size="lg" data-testid="home-sign-in" onClick={() => entry.signIn?.()}>
              Sign in
            </Button>
            <p style={NOTE_STYLE}>
              This server is for one organisation. Sign in with your work account to play.
            </p>
          </div>
        )}

        {/* **The primary CTA.** The sandbox is the low-commitment entry and the ranked one: the same
            seed and the same three rivals every time, which is what makes one player's score mean the
            same thing as another's. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Button
            size="lg"
            data-testid="home-practice-action"
            disabled={practice.kind === 'busy'}
            onClick={() => void beginPractice()}
          >
            {practice.kind === 'busy' ? 'Opening…' : 'Play in sandbox'}
          </Button>
          <p data-testid="home-practice-note" style={NOTE_STYLE}>
            For honing your vibe-coding skills. One colony, three rivals, the same field every time —
            so a better score means a better script.
          </p>
          {practiceNotice !== null && (
            <div data-testid="home-practice-notice" role="alert" style={REFUSAL_WARN_STYLE}>
              {practiceNotice}
            </div>
          )}
        </div>

        <div style={FOOT_STYLE}>
          <Button
            variant="secondary"
            size="lg"
            data-testid="home-leaderboard-action"
            onClick={() => entry.openLeaderboard?.()}
          >
            Leaderboard
          </Button>
          <p data-testid="home-leaderboard-note" style={NOTE_STYLE}>
            Everyone’s best sandbox run, and the prompt behind it.
          </p>
        </div>
      </div>

      {practice.kind === 'open' && (
        <PracticeSheet
          surface={practice.surface}
          now={now}
          busy={practice.busy}
          onClose={() => setPractice({ kind: 'closed' })}
          onStartOver={() => void startOverPractice()}
        />
      )}
    </div>
  );
}

const SCREEN_STYLE: CSSProperties = {
  position: 'relative',
  minHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  background: 'var(--surface-app)',
};

const BACKGROUND_STYLE: CSSProperties = { position: 'absolute', inset: 0, overflow: 'hidden' };

/** The chosen radial veil from `background-legibility.html` panel C, at its measured stops:
 * clear at the top where the hive and the densest bee traffic sit, opaque paper under the
 * controls. */
const VEIL_STYLE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  // Built from `veil.ts`'s declared stops, never hand-copied: the contrast the tests measure and
  // the veil the player sees are then the same numbers by construction.
  background: veilCss(),
};

const HEADER_SCRIM_STYLE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  background: headerScrimCss(),
};

/**
 * **Centred content column (re-capped 2026-08-07).** UI/UX audit **F-11** originally capped this
 * stack at 520px; a later pass reverted the cap for Home "on request" and left a comment here
 * warning a future reader off restoring it. That reversal is being undone: at a 1280px desktop
 * viewport the full-bleed stack produces ~1220px-wide buttons that read as broken chrome, not an
 * intended wide look, and the ambient background (a sibling of this stack, not a child) already fills
 * the full viewport on its own — nothing here was buying it space.
 *
 * `max-width` + `margin-inline: auto` rather than a media query: below the cap, `width: 100%`
 * already fills the viewport (two CSS properties standing in for a breakpoint), so phones and
 * narrow tablets are untouched and the column only centres once there is width to spare.
 */
const STACK_STYLE: CSSProperties = {
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  gap: 10,
  width: '100%',
  // `width: 100%` is the outer column, including its mobile gutters. Without border-box the
  // 40px inline padding is added outside that width, clipping every full-width card/control at
  // the right viewport edge on phones.
  boxSizing: 'border-box',
  maxWidth: 600,
  marginInline: 'auto',
  padding: '44px 20px 24px',
};

const WORDMARK_STYLE: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 80,
  letterSpacing: '-0.5px',
  lineHeight: 1.05,
  color: 'var(--ink-1)',
  margin: 0,
};

const BRANDING_STYLE: CSSProperties = {
  position: 'absolute',
  top: '30%',
  left: 20,
  right: 20,
  transform: 'translateY(-50%)',
  textAlign: 'center',
};

const FESTIVAL_LOGO_STYLE: CSSProperties = {
  width: 250,
  maxWidth: '68vw',
  height: 'auto',
  display: 'block',
  margin: '0 auto 18px',
};

const REFUSAL_WARN_STYLE: CSSProperties = {
  borderRadius: 'var(--r-md)',
  padding: '9px 11px',
  fontSize: 12,
  lineHeight: 1.4,
  background: 'var(--danger-soft)',
  border: '1px solid var(--danger)',
  color: '#7E2C1B',
};

const FOOT_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  paddingTop: 14,
};

/** `--ink-2`, not `--ink-3`. At 11px this is body text for AA purposes, and `--ink-3` at the bottom
 * of the screen, where the veil is thickest, computes to 3.87:1. */
const NOTE_STYLE: CSSProperties = {
  fontSize: 11,
  color: 'var(--ink-2)',
  textAlign: 'center',
  lineHeight: 1.45,
  margin: 0,
};
